import { useZinsserStore } from '@/stores/useZinsserStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Typography from '@tiptap/extension-typography'
import Placeholder from '@tiptap/extension-placeholder'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import Image from '@tiptap/extension-image'
import { Markdown } from 'tiptap-markdown'
import { marked } from 'marked'
import { useEffect, useMemo, useRef, useCallback, useState } from 'react'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { Addition, Deletion, Note, Flash, parseAnnotations, serializeAnnotations, acceptAnnotations, hasAnnotations, acceptSingleAnnotation, rejectSingleAnnotation, stripFlashMarkers, hasFlashMarkers } from '@/components/editor/AnnotationExtension'
import { EditorToolbar } from '@/components/editor/EditorToolbar'
import { AnnotationTooltip } from '@/components/editor/AnnotationTooltip'
import { WritingStats } from '@/components/editor/WritingStats'
import { updateZinsserSession, streamCutClutter, streamStrengthenVerbs, streamRhythm, streamCheckLogic, streamVoice } from '@/lib/api'

// Configure marked to allow HTML passthrough for <ins>/<del> annotations
// CRITICAL: We need a custom tokenizer extension to preserve <ins>/<del> tags
// because marked sanitizes HTML by default
const insDelExtension = {
  name: 'insDel',
  level: 'inline' as const,
  start(src: string) {
    return src.match(/<(ins|del)>/)?.index
  },
  tokenizer(src: string) {
    const insMatch = src.match(/^<ins>([\s\S]*?)<\/ins>/)
    if (insMatch) {
      return {
        type: 'insDel',
        raw: insMatch[0],
        tag: 'ins',
        text: insMatch[1],
      }
    }
    const delMatch = src.match(/^<del>([\s\S]*?)<\/del>/)
    if (delMatch) {
      return {
        type: 'insDel',
        raw: delMatch[0],
        tag: 'del',
        text: delMatch[1],
      }
    }
    return undefined
  },
  renderer(token: { tag: string; text: string }) {
    return `<${token.tag}>${token.text}</${token.tag}>`
  },
}

marked.use({
  breaks: true,
  gfm: true,
  extensions: [insDelExtension],
})

// Auto-save configuration
const AUTO_SAVE_DELAY = 1500 // 1.5 seconds debounce
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null

// Convert markdown to plain text for selection matching
// TipTap's textBetween() returns rendered text without markdown syntax,
// so we need to strip markdown formatting to match
/**
 * Find selectedText in markdown, returning positions in the ORIGINAL markdown string.
 *
 * Strips formatting from both sides for matching, but maps the found position
 * back to the original markdown so the backend gets correct splice points.
 */
function findSelectionInMarkdown(
  markdown: string,
  selectedText: string
): { start: number; end: number } | null {
  // Build a stripped version of markdown with a position map back to the original
  const stripped: string[] = []
  const posMap: number[] = [] // stripped index → original markdown index

  let i = 0
  while (i < markdown.length) {
    const ch = markdown[i]
    const rest = markdown.slice(i)

    // Skip HTML tags
    if (ch === '<') {
      const tagEnd = markdown.indexOf('>', i)
      if (tagEnd !== -1) { i = tagEnd + 1; continue }
    }

    // Skip markdown bold/italic markers
    if (rest.startsWith('**') || rest.startsWith('__')) { i += 2; continue }
    if ((ch === '*' || ch === '_') && i + 1 < markdown.length && markdown[i + 1] !== ' ') { i++; continue }

    // Skip header markers at start of line
    if (ch === '#' && (i === 0 || markdown[i - 1] === '\n')) {
      while (i < markdown.length && markdown[i] === '#') i++
      if (i < markdown.length && markdown[i] === ' ') i++ // skip space after #
      continue
    }

    // Skip backslashes (hard line breaks)
    if (ch === '\\') { i++; continue }

    // Skip list markers at start of line (- , * , + , 1. )
    if ((i === 0 || markdown[i - 1] === '\n') && (ch === '-' || ch === '+') && markdown[i + 1] === ' ') {
      i += 2; continue
    }
    if ((i === 0 || markdown[i - 1] === '\n') && ch >= '0' && ch <= '9') {
      let j = i
      while (j < markdown.length && markdown[j] >= '0' && markdown[j] <= '9') j++
      if (j < markdown.length && markdown[j] === '.' && markdown[j + 1] === ' ') {
        i = j + 2; continue
      }
    }

    // Normalize smart quotes
    const code = ch.charCodeAt(0)
    if (code === 0x2018 || code === 0x2019 || code === 0x201A || code === 0x201B) {
      posMap.push(i); stripped.push("'"); i++; continue
    }
    if (code === 0x201C || code === 0x201D || code === 0x201E || code === 0x201F) {
      posMap.push(i); stripped.push('"'); i++; continue
    }
    if (code === 0x00A0) { posMap.push(i); stripped.push(' '); i++; continue }
    if (code === 0x2013) { posMap.push(i); stripped.push('-'); i++; continue }
    if (code === 0x2014) { posMap.push(i); stripped.push('-'); i++; continue }
    if (code === 0x2026) { posMap.push(i); stripped.push('...'); i++; continue }

    // Collapse multiple newlines
    if (ch === '\n') {
      if (stripped.length === 0 || stripped[stripped.length - 1] !== '\n') {
        posMap.push(i)
        stripped.push('\n')
      }
      i++; continue
    }

    posMap.push(i)
    stripped.push(ch)
    i++
  }

  const strippedStr = stripped.join('')

  // Normalize the selected text the same way
  const normalizedSel = selectedText
    .replace(/\\/g, '')
    .replace(/\n+/g, '\n')
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/\u00A0/g, ' ')
    .replace(/\u2013/g, '-')
    .replace(/\u2014/g, '-')
    .replace(/\u2026/g, '...')

  let idx = strippedStr.indexOf(normalizedSel)

  // Fallback: try trimmed
  let matchLen = normalizedSel.length
  if (idx === -1 && normalizedSel.trim()) {
    const trimmed = normalizedSel.trim()
    idx = strippedStr.indexOf(trimmed)
    matchLen = trimmed.length
  }

  if (idx === -1) return null

  const startInOriginal = posMap[idx]
  // For end: find the original position of the last matched char, then +1
  const endIdx = idx + matchLen - 1
  const endInOriginal = endIdx < posMap.length ? posMap[endIdx] + 1 : markdown.length

  return { start: startInOriginal, end: endInOriginal }
}

interface ZinsserBrainstormDocumentContentProps {
  editable?: boolean
}

export function ZinsserBrainstormDocumentContent({ editable: editableProp = true }: ZinsserBrainstormDocumentContentProps) {
  const {
    sessionId,
    brainstormContent,
    brainstormDisplayContent,
    brainstormDocumentStreaming,
    brainstormDocumentPending,
    brainstormStreamingLineCount,
    brainstormIsThinking,
    brainstormStreamingThinking,
    brainstormStreamingContent,
    workflowStatus,
    updateBrainstormContent,
    // Annotation state
    zinsserDocAnnotated,
    showAnnotations,
    // Cut Clutter standalone workflow actions
    startCutClutterProcessing,
    startCutClutterThinking,
    updateCutClutterThinking,
    finishCutClutterThinking,
    finishStandaloneCutClutter,
    // Strengthen Verbs standalone workflow actions
    startStrengthenVerbsProcessing,
    startStrengthenVerbsThinking,
    updateStrengthenVerbsThinking,
    finishStrengthenVerbsThinking,
    finishStandaloneStrengthenVerbs,
    // Rhythm standalone workflow actions
    startRhythmProcessing,
    startRhythmThinking,
    updateRhythmThinking,
    finishRhythmThinking,
    finishStandaloneRhythm,
    // CheckLogic standalone workflow actions
    startCheckLogicProcessing,
    startCheckLogicThinking,
    updateCheckLogicThinking,
    finishCheckLogicThinking,
    finishStandaloneCheckLogic,
    // Voice standalone workflow actions
    startVoiceProcessing,
    startVoiceThinking,
    updateVoiceThinking,
    finishVoiceThinking,
    finishStandaloneVoice,
    applyCanvasEdit,
    // Tab navigation
    setMobileView,
  } = useZinsserStore()
  const { levineVoiceEnabled } = useAuthStore()
  const isMobile = useIsMobile()
  const [copied, setCopied] = useState(false)

  // Context menu state (for Cut Clutter / Strengthen Verbs right-click actions)
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    selectionStart: number
    selectionEnd: number
  } | null>(null)
  const [isCutClutterRunning, setIsCutClutterRunning] = useState(false)
  const [isStrengthenVerbsRunning, setIsStrengthenVerbsRunning] = useState(false)
  const [isRhythmRunning, setIsRhythmRunning] = useState(false)
  const [isCheckLogicRunning, setIsCheckLogicRunning] = useState(false)
  const [isVoiceRunning, setIsVoiceRunning] = useState(false)

  // Track last saved content to prevent redundant saves and empty content wipes
  const lastSavedContentRef = useRef<string | null>(null)

  // Lock editing during document streaming (per design decision)
  const editable = editableProp && !brainstormDocumentStreaming && !brainstormDocumentPending

  // Document display: Use transient displayContent if available, fall back to persisted content
  // - brainstormDisplayContent: Set by finishBrainstormDocumentStreaming (not persisted, avoids race conditions)
  // - brainstormContent: Persisted to localStorage, set after streaming is fully complete
  // - Annotations (==additions==, ~~deletions~~) are rendered as highlights/strikethroughs
  const displayContent = brainstormDisplayContent ?? brainstormContent

  const editorContainerRef = useRef<HTMLDivElement>(null)
  const prevContentRef = useRef<string>('')
  const isExternalUpdateRef = useRef<boolean>(false)
  const isUserEditingRef = useRef<boolean>(false)

  // Reset user editing flag when workflow starts streaming (AI takes control)
  useEffect(() => {
    if (workflowStatus === 'streaming' || brainstormDocumentStreaming) {
      isUserEditingRef.current = false
    }
  }, [workflowStatus, brainstormDocumentStreaming])

  // Strip card UUIDs from display (keep in raw content for tracking)
  const stripCardIds = (content: string): string => {
    return content.replace(/\s*\[[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\]/gi, '')
  }

  // Fix TipTap's broken list serialization: it removes newlines before list items
  // "**Questions:**- Item 1 - Item 2" → "**Questions:**\n\n- Item 1\n- Item 2"
  // Note: We use \n\n (blank line) before lists for proper CommonMark rendering
  const fixListNewlines = (content: string): string => {
    let fixed = content

    // Fix "text:**- item" or "text:- item" → blank line before unordered list
    fixed = fixed.replace(/(:\*{0,2})\s*-\s+/g, '$1\n\n- ')

    // Fix inline consecutive unordered list items "? - Item" or ". - Item"
    fixed = fixed.replace(/([.?!])\s+-\s+(?=[A-Z])/g, '$1\n- ')

    // Fix "text:**1. item" or "text:1. item" → blank line before numbered list
    fixed = fixed.replace(/(:\*{0,2})\s*(\d+\.)\s+/g, '$1\n\n$2 ')

    // Fix inline consecutive numbered list items - "First 2. Second" → "First\n2. Second"
    fixed = fixed.replace(/([a-z])\s+(\d+\.)\s+(?=[A-Z])/g, '$1\n$2 ')

    // Fix heading followed immediately by list "## Heading- Item" → "## Heading\n\n- Item"
    fixed = fixed.replace(/(#{1,6}\s+\S[^\n]*?)(\s*-\s+)/g, '$1\n\n- ')

    // Fix remaining inline list items: "Item 1 - Item 2" where Item 1 ends with digit
    fixed = fixed.replace(/(\d)\s+-\s+(?=[A-Z])/g, '$1\n- ')

    return fixed
  }

  // Normalize markdown list indentation to 4-space for CommonMark compatibility
  // markdown-it (used by tiptap-markdown) follows CommonMark which requires 4-space indentation
  // TipTap outputs 1-space indentation per level, we need to convert to 4-space
  const normalizeListIndentation = (content: string): string => {
    const lines = content.split('\n')
    const result: string[] = []

    for (const line of lines) {
      // Match list items with any leading whitespace
      const match = line.match(/^(\s*)([-*+]|\d+\.)\s/)
      if (match) {
        const currentIndent = match[1].length
        // If indent is not a multiple of 4, normalize it
        // 1 space = level 1 = 4 spaces, 2 spaces = level 1 = 4 spaces, 3 spaces = level 1 = 4 spaces
        // 4 spaces = level 1 = 4 spaces (already correct)
        // 5-7 spaces = level 2 = 8 spaces, etc.
        if (currentIndent > 0 && currentIndent % 4 !== 0) {
          // Calculate the nesting level (1 space = level 1, 2 spaces = level 1, etc.)
          const level = Math.ceil(currentIndent / 4)
          const newIndent = '    '.repeat(level)
          const rest = line.slice(match[1].length)
          result.push(newIndent + rest)
        } else {
          result.push(line)
        }
      } else {
        result.push(line)
      }
    }

    return result.join('\n')
  }

  // Parse annotation markers to HTML before passing to TipTap
  const parsedContent = useMemo(() => {
    if (!displayContent) return ''
    const strippedContent = stripCardIds(displayContent)
    // Normalize 2-space to 4-space indentation for CommonMark-compliant nested lists
    const normalizedContent = normalizeListIndentation(strippedContent)
    return parseAnnotations(normalizedContent)
  }, [displayContent])

  // Convert annotated markdown (with <ins>/<del> tags) to HTML for direct rendering
  const annotatedHtmlContent = useMemo(() => {
    if (!zinsserDocAnnotated) return ''
    return marked.parse(zinsserDocAnnotated, { async: false }) as string
  }, [zinsserDocAnnotated])

  // Determine if we should use direct HTML rendering (for <ins>/<del> annotations)
  const useDirectRender = showAnnotations && zinsserDocAnnotated

  // Auto-save to database with debounce
  // CRITICAL: Only save if content is non-empty and different from last saved
  const autoSave = useCallback(async (content: string) => {
    if (!sessionId) return

    // Don't save empty content - this prevents wiping DB on hydration race
    if (!content || content.trim() === '') return

    // Don't save if content hasn't changed from last save
    if (lastSavedContentRef.current === content) return

    try {
      await updateZinsserSession(sessionId, {
        new_doc: content,  // Save to new_doc (current canvas content)
      })
      lastSavedContentRef.current = content
    } catch (err) {
      console.error('[ZinsserBrainstormDoc] Auto-save failed:', err)
    }
  }, [sessionId])

  // Handle user edits - sync back to store and trigger auto-save
  const handleEditorUpdate = useCallback(({ editor }: { editor: ReturnType<typeof useEditor> }) => {
    // Don't process edits during external updates or when not editable
    if (isExternalUpdateRef.current || !editable) return

    // Use tiptap-markdown to get proper markdown output (not HTML)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor?.storage as any
    if (storage?.markdown?.getMarkdown) {
      const markdown = storage.markdown.getMarkdown()
      // Convert HTML annotation marks back to markdown format
      const serialized = serializeAnnotations(markdown)
      // Fix TipTap's broken list serialization (missing newlines before list items)
      const fixedLists = fixListNewlines(serialized)
      // Normalize list indentation: TipTap outputs 1-space, CommonMark needs 4-space
      const normalized = normalizeListIndentation(fixedLists)

      if (normalized !== brainstormContent) {
        // Only mark as user editing if content actually changed (real user edit)
        // This prevents blocking external updates from workflow
        isUserEditingRef.current = true
        updateBrainstormContent(normalized)

        // Debounced auto-save to database
        if (autoSaveTimer) {
          clearTimeout(autoSaveTimer)
        }
        autoSaveTimer = setTimeout(() => {
          autoSave(normalized)
          // Reset user editing flag after auto-save completes
          isUserEditingRef.current = false
        }, AUTO_SAVE_DELAY)
      }
    }
  }, [brainstormContent, updateBrainstormContent, autoSave, editable])

  // Handle individual annotation accept (from hover tooltip)
  const handleAnnotationAccept = useCallback((element: HTMLElement, type: 'addition' | 'deletion' | 'note') => {
    if (!brainstormContent) return

    // Reset user editing flag so setContent can run
    isUserEditingRef.current = false

    const annotationText = element.textContent || ''
    const updatedContent = acceptSingleAnnotation(brainstormContent, annotationText, type)

    if (updatedContent !== brainstormContent) {
      updateBrainstormContent(updatedContent)
      autoSave(updatedContent)
    }
  }, [brainstormContent, updateBrainstormContent, autoSave])

  // Handle individual annotation reject (from hover tooltip)
  const handleAnnotationReject = useCallback((element: HTMLElement, type: 'addition' | 'deletion' | 'note') => {
    if (!brainstormContent) return

    // Reset user editing flag so setContent can run
    isUserEditingRef.current = false

    const annotationText = element.textContent || ''
    const updatedContent = rejectSingleAnnotation(brainstormContent, annotationText, type)

    if (updatedContent !== brainstormContent) {
      updateBrainstormContent(updatedContent)
      autoSave(updatedContent)
    }
  }, [brainstormContent, updateBrainstormContent, autoSave])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        // Enable backspace at start of heading to convert to paragraph
      }),
      Markdown.configure({
        html: true,  // Allow HTML tags within markdown (for annotation marks)
        transformPastedText: true,
        transformCopiedText: true,
        linkify: true,  // Parse markdown links [text](url)
        breaks: true,  // Convert single newlines to <br> (like GitHub-flavored markdown)
      }),
      Placeholder.configure({
        placeholder: 'Begin writing...',
      }),
      Link.extend({
        inclusive: false,
        exitable: true,
      }).configure({
        openOnClick: false,
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Typography,
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Image.configure({
        inline: false,
        allowBase64: true,  // Allow pasted images as base64
      }),
      Addition,
      Deletion,
      Note,
      Flash,  // Temporary highlight for real-time edits
    ],
    content: parsedContent || '',  // Initialize with content (matches develop pattern)
    editable,
    editorProps: {
      attributes: {
        class: 'prose-zinsser outline-none min-h-full',
      },
      handleKeyDown: (view, event) => {
        // Cmd+K to add link (shows text only, URL hidden)
        if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault()
          const { state } = view
          const { from, to } = state.selection
          const selectedText = state.doc.textBetween(from, to, '')

          if (!selectedText) {
            // No selection - prompt for both text and URL
            const text = window.prompt('Link text:')
            if (!text) return true
            const url = window.prompt('URL:')
            if (!url) return true

            let href = url.trim()
            if (href && !href.match(/^[a-z]+:\/\//i)) {
              href = 'https://' + href
            }

            const linkMark = state.schema.marks.link.create({ href })
            const linkNode = state.schema.text(text, [linkMark])
            view.dispatch(state.tr.replaceWith(from, to, linkNode))
          } else {
            // Has selection - just prompt for URL
            const url = window.prompt('URL:')
            if (!url) return true

            let href = url.trim()
            if (href && !href.match(/^[a-z]+:\/\//i)) {
              href = 'https://' + href
            }

            const linkMark = state.schema.marks.link.create({ href })
            view.dispatch(state.tr.addMark(from, to, linkMark))
          }
          return true
        }

        // Backspace at start of heading converts to paragraph
        if (event.key === 'Backspace') {
          const { state } = view
          const { selection } = state
          const { $from } = selection

          // Check if cursor is at start of a heading
          if ($from.parentOffset === 0 && $from.parent.type.name === 'heading') {
            // Convert heading to paragraph
            const tr = state.tr.setBlockType($from.pos - 1, $from.pos, state.schema.nodes.paragraph)
            view.dispatch(tr)
            return true
          }
        }
        return false
      },
    },
    onUpdate: handleEditorUpdate,
    immediatelyRender: false,
  }, [])  // Empty deps - only create editor once

  // Update editable state when prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable)
    }
  }, [editor, editable])

  // Handler for link button in toolbar
  const handleLinkClick = useCallback(() => {
    if (!editor) return

    const { from, to } = editor.state.selection
    const selectedText = editor.state.doc.textBetween(from, to, '')

    if (!selectedText) {
      // No selection - prompt for both text and URL
      const text = window.prompt('Link text:')
      if (!text) return
      const url = window.prompt('URL:')
      if (!url) return

      let href = url.trim()
      if (href && !href.match(/^[a-z]+:\/\//i)) {
        href = 'https://' + href
      }

      editor.chain().focus().insertContent({
        type: 'text',
        text: text,
        marks: [{ type: 'link', attrs: { href } }],
      }).run()
    } else {
      // Has selection - just prompt for URL
      const url = window.prompt('URL:')
      if (!url) return

      let href = url.trim()
      if (href && !href.match(/^[a-z]+:\/\//i)) {
        href = 'https://' + href
      }

      editor.chain().focus().setLink({ href }).run()
    }
  }, [editor])

  // Handle right-click context menu for Cut Clutter
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    // Only show context menu if there's a selection and editor is ready
    if (!editor) return

    const { from, to } = editor.state.selection
    const hasSelection = from !== to

    if (!hasSelection) {
      // No selection - let browser handle default context menu
      return
    }

    e.preventDefault()

    // For now, we pass the TipTap positions - the backend will work with the full document
    // and selection boundaries to extract the right text
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      selectionStart: from,
      selectionEnd: to,
    })
  }, [editor])

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClick = () => setContextMenu(null)
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null)
    }
    document.addEventListener('click', handleClick)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('click', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // Execute Cut Clutter on selected text
  const handleCutClutter = useCallback(async () => {
    if (!sessionId || !brainstormContent || !contextMenu) return

    setContextMenu(null)
    setIsCutClutterRunning(true)

    // Get selection boundaries from context menu
    // Convert TipTap positions to character offsets in the markdown
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor?.storage as any
    const markdown = storage?.markdown?.getMarkdown?.() || brainstormContent

    // TipTap's positions don't directly map to markdown character positions
    // Use newline as block separator since markdown uses newlines between paragraphs
    // Use the editor's current selection — contextMenu positions can be stale
    // after a previous micro-edit rewrites the document text
    const { from: selFrom, to: selTo } = editor?.state.selection || { from: contextMenu.selectionStart, to: contextMenu.selectionEnd }
    const selectedText = editor?.state.doc.textBetween(
      selFrom,
      selTo,
      '\n',
      '\n'  // Replace hardBreak leaf nodes with \n (markdown serializes these as \)
    ) || ''

    // Strip annotation markers (==highlights==, ~~strikethroughs~~) from markdown
    // The editor shows plain text but markdown may contain these markers from previous edits
    // We send the clean markdown to the backend so positions match
    const cleanMarkdown = markdown
      // Strip annotation deletions entirely (text + tags)
      .replace(/<mark[^>]*annotation-deletion[^>]*>[\s\S]*?<\/mark>/g, '')
      .replace(/~~[\s\S]*?~~/g, '')
      // Unwrap annotation additions (keep text, strip tags)
      .replace(/<mark[^>]*annotation-addition[^>]*>([\s\S]*?)<\/mark>/g, '$1')
      .replace(/==([\s\S]*?)==/g, '$1')
      // Strip any remaining HTML tags
      .replace(/<[^>]+>/g, '')

    const match = findSelectionInMarkdown(cleanMarkdown, selectedText)
    if (!match) {
      console.error('[CutClutter] Could not find selection in markdown', { cleanMarkdown, selectedText })
      setIsCutClutterRunning(false)
      return
    }
    const { start: selectionStart, end: selectionEnd } = match

    // Start processing state
    startCutClutterProcessing()

    try {
      await streamCutClutter(
        {
          session_id: sessionId,
          new_doc: cleanMarkdown,
          selection_start: selectionStart,
          selection_end: selectionEnd,
          run_mode: 'fast',
        },
        {
          onThinkingStart: () => {
            startCutClutterThinking()
          },
          onThinkingDelta: (delta) => {
            updateCutClutterThinking(delta)
          },
          onThinkingComplete: () => {
            finishCutClutterThinking()
          },
          onEdit: (oldStr, newStr) => {
            // Apply real-time edit to canvas with flash highlighting
            applyCanvasEdit(oldStr, newStr)
          },
          onDone: () => {
            // CutClutter node done - wait for session_complete
          },
          onSessionComplete: (output) => {
            setIsCutClutterRunning(false)

            // Use store action to create turn and update state
            finishStandaloneCutClutter({
              new_doc: output.new_doc || markdown,
              annotated_doc: output.annotated_doc,
              final_diff: output.final_diff,
            })

            // Auto-save the updated document
            if (output.new_doc) {
              autoSave(output.new_doc)
            }

            // Auto-switch to Edit tab to show changes
            setMobileView('document')
          },
          onError: (error) => {
            console.error('[CutClutter] Error:', error)
            setIsCutClutterRunning(false)
          },
        }
      )
    } catch (err) {
      console.error('[CutClutter] Failed:', err)
      setIsCutClutterRunning(false)
    }
  }, [sessionId, brainstormContent, contextMenu, editor, startCutClutterProcessing, startCutClutterThinking, updateCutClutterThinking, finishCutClutterThinking, applyCanvasEdit, finishStandaloneCutClutter, autoSave, setMobileView])

  // Execute Strengthen Verbs on selected text
  const handleStrengthenVerbs = useCallback(async () => {
    if (!sessionId || !brainstormContent || !contextMenu) return

    setContextMenu(null)
    setIsStrengthenVerbsRunning(true)

    // Get selection boundaries from context menu
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor?.storage as any
    const markdown = storage?.markdown?.getMarkdown?.() || brainstormContent

    // TipTap's positions don't directly map to markdown character positions
    // Use newline as block separator since markdown uses newlines between paragraphs
    // Use the editor's current selection — contextMenu positions can be stale
    // after a previous micro-edit rewrites the document text
    const { from: selFrom, to: selTo } = editor?.state.selection || { from: contextMenu.selectionStart, to: contextMenu.selectionEnd }
    const selectedText = editor?.state.doc.textBetween(
      selFrom,
      selTo,
      '\n',
      '\n'  // Replace hardBreak leaf nodes with \n (markdown serializes these as \)
    ) || ''

    // Strip annotation markers (==highlights==, ~~strikethroughs~~) from markdown
    // The editor shows plain text but markdown may contain these markers from previous edits
    // We send the clean markdown to the backend so positions match
    const cleanMarkdown = markdown
      // Strip annotation deletions entirely (text + tags)
      .replace(/<mark[^>]*annotation-deletion[^>]*>[\s\S]*?<\/mark>/g, '')
      .replace(/~~[\s\S]*?~~/g, '')
      // Unwrap annotation additions (keep text, strip tags)
      .replace(/<mark[^>]*annotation-addition[^>]*>([\s\S]*?)<\/mark>/g, '$1')
      .replace(/==([\s\S]*?)==/g, '$1')
      // Strip any remaining HTML tags
      .replace(/<[^>]+>/g, '')

    const match = findSelectionInMarkdown(cleanMarkdown, selectedText)
    if (!match) {
      console.error('[StrengthenVerbs] Could not find selection in markdown', { cleanMarkdown, selectedText })
      setIsStrengthenVerbsRunning(false)
      return
    }
    const { start: selectionStart, end: selectionEnd } = match

    // Start processing state
    startStrengthenVerbsProcessing()

    try {
      await streamStrengthenVerbs(
        {
          session_id: sessionId,
          new_doc: cleanMarkdown,
          selection_start: selectionStart,
          selection_end: selectionEnd,
          run_mode: 'fast',
        },
        {
          onThinkingStart: () => {
            startStrengthenVerbsThinking()
          },
          onThinkingDelta: (delta) => {
            updateStrengthenVerbsThinking(delta)
          },
          onThinkingComplete: () => {
            finishStrengthenVerbsThinking()
          },
          onEdit: (oldStr, newStr) => {
            applyCanvasEdit(oldStr, newStr)
          },
          onDone: () => {
            // StrengthenVerbs node done - wait for session_complete
          },
          onSessionComplete: (output) => {
            setIsStrengthenVerbsRunning(false)

            finishStandaloneStrengthenVerbs({
              new_doc: output.new_doc || markdown,
              annotated_doc: output.annotated_doc,
              final_diff: output.final_diff,
            })

            if (output.new_doc) {
              autoSave(output.new_doc)
            }

            // Auto-switch to Edit tab to show changes
            setMobileView('document')
          },
          onError: (error) => {
            console.error('[StrengthenVerbs] Error:', error)
            setIsStrengthenVerbsRunning(false)
          },
        }
      )
    } catch (err) {
      console.error('[StrengthenVerbs] Failed:', err)
      setIsStrengthenVerbsRunning(false)
    }
  }, [sessionId, brainstormContent, contextMenu, editor, startStrengthenVerbsProcessing, startStrengthenVerbsThinking, updateStrengthenVerbsThinking, finishStrengthenVerbsThinking, applyCanvasEdit, finishStandaloneStrengthenVerbs, autoSave, setMobileView])

  // Execute Rhythm on selected text
  const handleRhythm = useCallback(async () => {
    if (!sessionId || !brainstormContent || !contextMenu) return

    setContextMenu(null)
    setIsRhythmRunning(true)

    // Get selection boundaries from context menu
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor?.storage as any
    const markdown = storage?.markdown?.getMarkdown?.() || brainstormContent

    // TipTap's positions don't directly map to markdown character positions
    // Use newline as block separator since markdown uses newlines between paragraphs
    // Use the editor's current selection — contextMenu positions can be stale
    // after a previous micro-edit rewrites the document text
    const { from: selFrom, to: selTo } = editor?.state.selection || { from: contextMenu.selectionStart, to: contextMenu.selectionEnd }
    const selectedText = editor?.state.doc.textBetween(
      selFrom,
      selTo,
      '\n',
      '\n'  // Replace hardBreak leaf nodes with \n (markdown serializes these as \)
    ) || ''

    // Strip annotation markers (==highlights==, ~~strikethroughs~~) from markdown
    // The editor shows plain text but markdown may contain these markers from previous edits
    // We send the clean markdown to the backend so positions match
    const cleanMarkdown = markdown
      // Strip annotation deletions entirely (text + tags)
      .replace(/<mark[^>]*annotation-deletion[^>]*>[\s\S]*?<\/mark>/g, '')
      .replace(/~~[\s\S]*?~~/g, '')
      // Unwrap annotation additions (keep text, strip tags)
      .replace(/<mark[^>]*annotation-addition[^>]*>([\s\S]*?)<\/mark>/g, '$1')
      .replace(/==([\s\S]*?)==/g, '$1')
      // Strip any remaining HTML tags
      .replace(/<[^>]+>/g, '')

    // Convert markdown to plain text (strips formatting like list markers, headers, etc.)
    const match = findSelectionInMarkdown(cleanMarkdown, selectedText)
    if (!match) {
      console.error('[Rhythm] Could not find selection in markdown', { cleanMarkdown, selectedText })
      setIsRhythmRunning(false)
      return
    }
    const { start: selectionStart, end: selectionEnd } = match

    // Start processing state
    startRhythmProcessing()

    try {
      await streamRhythm(
        {
          session_id: sessionId,
          new_doc: cleanMarkdown,
          selection_start: selectionStart,
          selection_end: selectionEnd,
          run_mode: 'fast',
        },
        {
          onThinkingStart: () => {
            startRhythmThinking()
          },
          onThinkingDelta: (delta) => {
            updateRhythmThinking(delta)
          },
          onThinkingComplete: () => {
            finishRhythmThinking()
          },
          onEdit: (oldStr, newStr) => {
            applyCanvasEdit(oldStr, newStr)
          },
          onDone: () => {
            // Rhythm node done - wait for session_complete
          },
          onSessionComplete: (output) => {
            setIsRhythmRunning(false)

            finishStandaloneRhythm({
              new_doc: output.new_doc || markdown,
              annotated_doc: output.annotated_doc,
              final_diff: output.final_diff,
            })

            if (output.new_doc) {
              autoSave(output.new_doc)
            }

            // Auto-switch to Edit tab to show changes
            setMobileView('document')
          },
          onError: (error) => {
            console.error('[Rhythm] Error:', error)
            setIsRhythmRunning(false)
          },
        }
      )
    } catch (err) {
      console.error('[Rhythm] Failed:', err)
      setIsRhythmRunning(false)
    }
  }, [sessionId, brainstormContent, contextMenu, editor, startRhythmProcessing, startRhythmThinking, updateRhythmThinking, finishRhythmThinking, applyCanvasEdit, finishStandaloneRhythm, autoSave, setMobileView])

  // Execute Check Logic on selected text
  const handleCheckLogic = useCallback(async () => {
    if (!sessionId || !brainstormContent || !contextMenu) return

    setContextMenu(null)
    setIsCheckLogicRunning(true)

    // Get selection boundaries from context menu
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor?.storage as any
    const markdown = storage?.markdown?.getMarkdown?.() || brainstormContent

    // TipTap's positions don't directly map to markdown character positions
    // Use newline as block separator since markdown uses newlines between paragraphs
    // Use the editor's current selection — contextMenu positions can be stale
    // after a previous micro-edit rewrites the document text
    const { from: selFrom, to: selTo } = editor?.state.selection || { from: contextMenu.selectionStart, to: contextMenu.selectionEnd }
    const selectedText = editor?.state.doc.textBetween(
      selFrom,
      selTo,
      '\n',
      '\n'  // Replace hardBreak leaf nodes with \n (markdown serializes these as \)
    ) || ''

    // Strip annotation markers (==highlights==, ~~strikethroughs~~) from markdown
    // The editor shows plain text but markdown may contain these markers from previous edits
    // We send the clean markdown to the backend so positions match
    const cleanMarkdown = markdown
      // Strip annotation deletions entirely (text + tags)
      .replace(/<mark[^>]*annotation-deletion[^>]*>[\s\S]*?<\/mark>/g, '')
      .replace(/~~[\s\S]*?~~/g, '')
      // Unwrap annotation additions (keep text, strip tags)
      .replace(/<mark[^>]*annotation-addition[^>]*>([\s\S]*?)<\/mark>/g, '$1')
      .replace(/==([\s\S]*?)==/g, '$1')
      // Strip any remaining HTML tags
      .replace(/<[^>]+>/g, '')

    // Convert markdown to plain text (strips formatting like list markers, headers, etc.)
    const match = findSelectionInMarkdown(cleanMarkdown, selectedText)
    if (!match) {
      console.error('[CheckLogic] Could not find selection in markdown', { cleanMarkdown, selectedText })
      setIsCheckLogicRunning(false)
      return
    }
    const { start: selectionStart, end: selectionEnd } = match

    // Start processing state
    startCheckLogicProcessing()

    try {
      await streamCheckLogic(
        {
          session_id: sessionId,
          new_doc: cleanMarkdown,
          selection_start: selectionStart,
          selection_end: selectionEnd,
          run_mode: 'fast',
        },
        {
          onThinkingStart: () => {
            startCheckLogicThinking()
          },
          onThinkingDelta: (delta) => {
            updateCheckLogicThinking(delta)
          },
          onThinkingComplete: () => {
            finishCheckLogicThinking()
          },
          onEdit: (oldStr, newStr) => {
            applyCanvasEdit(oldStr, newStr)
          },
          onDone: () => {
            // CheckLogic node done - wait for session_complete
          },
          onSessionComplete: (output) => {
            setIsCheckLogicRunning(false)

            finishStandaloneCheckLogic({
              new_doc: output.new_doc || markdown,
              annotated_doc: output.annotated_doc,
              final_diff: output.final_diff,
            })

            if (output.new_doc) {
              autoSave(output.new_doc)
            }

            // Auto-switch to Edit tab to show changes
            setMobileView('document')
          },
          onError: (error) => {
            console.error('[CheckLogic] Error:', error)
            setIsCheckLogicRunning(false)
          },
        }
      )
    } catch (err) {
      console.error('[CheckLogic] Failed:', err)
      setIsCheckLogicRunning(false)
    }
  }, [sessionId, brainstormContent, contextMenu, editor, startCheckLogicProcessing, startCheckLogicThinking, updateCheckLogicThinking, finishCheckLogicThinking, applyCanvasEdit, finishStandaloneCheckLogic, autoSave, setMobileView])

  // Handle Voice transformation (toolbar button - processes ENTIRE document)
  const handleVoice = useCallback(async () => {
    if (!sessionId || !brainstormContent) return

    setIsVoiceRunning(true)
    startVoiceProcessing()

    // On mobile, switch to Document tab to show edits being applied
    if (isMobile) {
      setMobileView('document')
    }

    // Get raw markdown from editor
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor?.storage as any
    const markdown = storage?.markdown?.getMarkdown?.() || brainstormContent

    try {
      await streamVoice(
        {
          session_id: sessionId,
          new_doc: markdown,
          run_mode: 'fast',
          // No selection - voice operates on full document
        },
        {
          onThinkingStart: () => {
            startVoiceThinking()
          },
          onThinkingDelta: (delta) => {
            updateVoiceThinking(delta)
          },
          onThinkingComplete: () => {
            finishVoiceThinking()
          },
          onEdit: (oldStr, newStr) => {
            // Apply each edit to the canvas in real-time
            applyCanvasEdit(oldStr, newStr)
          },
          onDone: () => {
            // Voice node finished - wait for session_complete
          },
          onSessionComplete: (output) => {
            // Workflow finished - update document with final result
            const newDoc = output.new_doc || markdown
            finishStandaloneVoice({
              new_doc: newDoc,
              annotated_doc: output.annotated_doc ?? undefined,
              final_diff: output.final_diff ?? undefined,
            })
            // Trigger auto-save with final document
            autoSave(newDoc)
            setIsVoiceRunning(false)
          },
          onError: (error) => {
            console.error('[Voice] Error:', error)
            setIsVoiceRunning(false)
          },
        }
      )
    } catch (err) {
      console.error('[Voice] Failed:', err)
      setIsVoiceRunning(false)
    }
  }, [sessionId, brainstormContent, editor, isMobile, startVoiceProcessing, startVoiceThinking, updateVoiceThinking, finishVoiceThinking, applyCanvasEdit, finishStandaloneVoice, autoSave, setMobileView])

  // Update editor content when content changes externally (streaming, Accept All, initial load)
  // CRITICAL: Skip updates when user is actively editing to prevent scroll jumping
  useEffect(() => {
    if (!editor) return

    const contentChanged = prevContentRef.current !== parsedContent
    prevContentRef.current = parsedContent

    // Get current editor state
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor?.storage as any
    const currentEditorMarkdown = storage?.markdown?.getMarkdown?.() || ''
    const editorIsEffectivelyEmpty = !currentEditorMarkdown.trim() || currentEditorMarkdown.trim().length < 5

    // CASE 1: User is actively editing - never interrupt them
    if (isUserEditingRef.current && !brainstormDocumentStreaming) {
      return
    }

    // CASE 2: No content change and not streaming - nothing to do
    if (!contentChanged && !brainstormDocumentStreaming) {
      return
    }

    // CASE 3: Editor is empty but we have content - ALWAYS apply (initial load / creation)
    if (editorIsEffectivelyEmpty && parsedContent && parsedContent.length > 10) {
      isExternalUpdateRef.current = true
      editor.commands.setContent(parsedContent, { emitUpdate: false })
      queueMicrotask(() => { isExternalUpdateRef.current = false })
      return
    }

    // CASE 4: Streaming - always update
    if (brainstormDocumentStreaming) {
      isExternalUpdateRef.current = true
      editor.commands.setContent(parsedContent || '', { emitUpdate: false })
      queueMicrotask(() => { isExternalUpdateRef.current = false })
      return
    }

    // CASE 5: Content changed - check if it's a user edit or external update
    const currentEditorContent = serializeAnnotations(currentEditorMarkdown)
    const strippedEditorContent = stripCardIds(currentEditorContent)
    const strippedDisplayContent = stripCardIds(displayContent || '')
    const editorHasAnnotations = hasAnnotations(currentEditorContent)
    const displayHasAnnotations = hasAnnotations(displayContent || '')

    // Annotation state changed (Accept All) - apply update
    if (editorHasAnnotations !== displayHasAnnotations) {
      isExternalUpdateRef.current = true
      editor.commands.setContent(parsedContent || '', { emitUpdate: false })
      queueMicrotask(() => { isExternalUpdateRef.current = false })
      return
    }

    // Check if content actually differs (user edit detection)
    const normalizedEditorContent = normalizeListIndentation(strippedEditorContent)
    const normalizedDisplayContent = normalizeListIndentation(strippedDisplayContent)

    if (normalizedEditorContent.trim() === normalizedDisplayContent.trim()) {
      // Content matches - this was a user edit, editor already has correct content
      return
    }

    // Content is different - apply external update
    isExternalUpdateRef.current = true
    editor.commands.setContent(parsedContent || '', { emitUpdate: false })
    queueMicrotask(() => { isExternalUpdateRef.current = false })

    // Trigger animations on new annotations
    if (contentChanged && parsedContent && editorContainerRef.current) {
      requestAnimationFrame(() => {
        const container = editorContainerRef.current
        if (!container) return

        const additions = container.querySelectorAll('.annotation-addition')
        const deletions = container.querySelectorAll('.annotation-deletion')

        additions.forEach(el => {
          el.classList.remove('annotation-animate')
          void (el as HTMLElement).offsetWidth
          el.classList.add('annotation-animate')
        })
        deletions.forEach(el => {
          el.classList.remove('annotation-animate')
          void (el as HTMLElement).offsetWidth
          el.classList.add('annotation-animate')
        })
      })
    }
  }, [editor, parsedContent, displayContent, brainstormDocumentStreaming])

  // Highlight last block during streaming for progress indication
  useEffect(() => {
    const container = editorContainerRef.current
    if (!container) return

    // Get all block-level elements (paragraphs, headings, list items)
    const blocks = container.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li')

    // If not streaming, remove all highlights
    if (!brainstormDocumentStreaming) {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
      return
    }

    // Remove highlight from all blocks first
    blocks.forEach(el => el.classList.remove('streaming-line-highlight'))

    // Highlight the LAST block element (where new content is being added)
    const lastBlock = blocks[blocks.length - 1]
    if (lastBlock) {
      lastBlock.classList.add('streaming-line-highlight')
      // Auto-scroll to keep the last block visible
      // On mobile, only scroll if keyboard is NOT open (to prevent jump when typing)
      const keyboardOpen = window.visualViewport &&
        (window.innerHeight - window.visualViewport.height) > 100
      if (!isMobile || !keyboardOpen) {
        lastBlock.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }

    // Cleanup: remove highlights when effect re-runs or unmounts
    return () => {
      blocks.forEach(el => el.classList.remove('streaming-line-highlight'))
    }
  }, [brainstormDocumentStreaming, brainstormStreamingLineCount])

  // Strip flash markers after animation completes (1.5s matches CSS animation duration)
  // This removes {{flash:id}}...{{/flash}} markers from content after the highlight fades
  useEffect(() => {
    if (!brainstormContent || !hasFlashMarkers(brainstormContent)) return

    const timer = setTimeout(() => {
      const cleanedContent = stripFlashMarkers(brainstormContent)
      if (cleanedContent !== brainstormContent) {
        updateBrainstormContent(cleanedContent)
      }
    }, 1600)  // Slightly longer than animation to ensure it completes

    return () => clearTimeout(timer)
  }, [brainstormContent, updateBrainstormContent])

  // Show editing indicator when:
  // 1. Old streaming mode (brainstormDocumentStreaming) - legacy
  // 2. JSON mode: thinking complete but no coaching message yet (AI is producing JSON)
  // 3. Cut Clutter is running (right-click context menu action)
  // 4. Strengthen Verbs is running (right-click context menu action)
  // 5. Rhythm is running (right-click context menu action)
  // 6. CheckLogic is running (right-click context menu action)
  // 7. Voice is running (toolbar button action)
  const isEditingDocument = brainstormDocumentStreaming || isCutClutterRunning || isStrengthenVerbsRunning || isRhythmRunning || isCheckLogicRunning || isVoiceRunning ||
    (workflowStatus === 'streaming' && !brainstormIsThinking && brainstormStreamingThinking && !brainstormStreamingContent?.coaching_message)

  // Get the editing indicator label
  const getEditingLabel = () => {
    if (isCutClutterRunning) return 'Cutting clutter...'
    if (isStrengthenVerbsRunning) return 'Strengthening verbs...'
    if (isRhythmRunning) return 'Varying rhythm...'
    if (isCheckLogicRunning) return 'Checking logic...'
    if (isVoiceRunning) return 'Transforming voice...'
    return 'Editing document...'
  }

  return (
    <div className="h-full flex flex-col">
      {/* Editing indicator - shows at top when AI is editing document */}
      {isEditingDocument && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-red-500/30 bg-red-500/10">
          <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="font-medium">
              {getEditingLabel()}
            </span>
          </div>
        </div>
      )}

      {/* Toolbar - only show when editable and not streaming */}
      {editable && !brainstormDocumentStreaming && (
        <div className="flex items-center border-b border-border">
          <div className="flex-1">
            <EditorToolbar editor={editor} disabled={!editable} onLinkClick={handleLinkClick} noBorder />
          </div>
          {/* Voice button - applies Levine style to entire document (feature-gated) */}
          {levineVoiceEnabled && (
            <div className="flex-shrink-0 px-3 py-2 border-l border-border">
              <button
                onClick={handleVoice}
                disabled={isVoiceRunning || isCutClutterRunning || isStrengthenVerbsRunning || isRhythmRunning || !sessionId || !brainstormContent}
                title="Transform voice to Levine style"
                className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors bg-violet-500/10 text-violet-600 dark:text-violet-400 hover:bg-violet-500/20 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isVoiceRunning ? 'Transforming...' : 'Levine Voice'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Document content - always show editor so user can write immediately */}
      <div
        className="flex-1 overflow-y-auto px-4 pt-3 pb-20 md:pb-[30vh] relative cursor-text"
        onClick={() => !useDirectRender && editor?.commands.focus()}
        onContextMenu={handleContextMenu}
      >
        {brainstormDocumentPending && !displayContent ? (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <div className="inline-block w-2 h-2 bg-primary rounded-full animate-pulse mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Building document...
              </p>
            </div>
          </div>
        ) : useDirectRender ? (
          /* Direct HTML render for <ins>/<del> annotations (read-only view) */
          <div
            className="prose-zinsser min-h-full"
            ref={editorContainerRef}
            dangerouslySetInnerHTML={{ __html: annotatedHtmlContent }}
          />
        ) : (
          <div className="min-h-full" ref={editorContainerRef}>
            <EditorContent editor={editor} className="min-h-full" />
          </div>
        )}

        {/* Hover tooltip for individual annotation accept/reject */}
        <AnnotationTooltip
          containerRef={editorContainerRef}
          onAccept={handleAnnotationAccept}
          onReject={handleAnnotationReject}
          disabled={brainstormDocumentStreaming || workflowStatus === 'streaming'}
        />

        {/* Right-click context menu for micro-editing actions */}
        {contextMenu && (
          <div
            className="fixed z-50 min-w-[180px] bg-popover border border-border rounded-md shadow-lg py-1"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button
              onClick={handleCutClutter}
              disabled={isCutClutterRunning || isStrengthenVerbsRunning || isRhythmRunning || isCheckLogicRunning || isVoiceRunning || !sessionId}
              className="w-full px-3 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {/* Scissors icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="6" cy="6" r="3" />
                <circle cx="6" cy="18" r="3" />
                <line x1="20" y1="4" x2="8.12" y2="15.88" />
                <line x1="14.47" y1="14.48" x2="20" y2="20" />
                <line x1="8.12" y1="8.12" x2="12" y2="12" />
              </svg>
              Cut Clutter
            </button>
            <button
              onClick={handleStrengthenVerbs}
              disabled={isCutClutterRunning || isStrengthenVerbsRunning || isRhythmRunning || isCheckLogicRunning || isVoiceRunning || !sessionId}
              className="w-full px-3 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {/* Bicep/strength icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                <line x1="4" y1="22" x2="4" y2="15" />
              </svg>
              Strengthen Verbs
            </button>
            <button
              onClick={handleRhythm}
              disabled={isCutClutterRunning || isStrengthenVerbsRunning || isRhythmRunning || isCheckLogicRunning || isVoiceRunning || !sessionId}
              className="w-full px-3 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {/* Music/rhythm icon (sound wave) */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 10v3" />
                <path d="M6 6v11" />
                <path d="M10 3v18" />
                <path d="M14 8v7" />
                <path d="M18 5v13" />
                <path d="M22 10v3" />
              </svg>
              Vary Rhythm
            </button>
            <button
              onClick={handleCheckLogic}
              disabled={isCutClutterRunning || isStrengthenVerbsRunning || isRhythmRunning || isCheckLogicRunning || isVoiceRunning || !sessionId}
              className="w-full px-3 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {/* Logic/flow icon (activity/pulse line) */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              Check Logic
            </button>
          </div>
        )}
      </div>

      {/* Writing stats - show when not streaming */}
      {!brainstormDocumentStreaming && workflowStatus !== 'streaming' && (
        <WritingStats content={brainstormContent || ''} />
      )}

      {/* Footer actions - show when not actively streaming */}
      {brainstormContent && workflowStatus !== 'streaming' && (
        <div className="flex-shrink-0 px-4 py-3 border-t border-border space-y-2">
          {/* Accept All Changes button - only show when annotations exist */}
          {hasAnnotations(brainstormContent) && (
            <button
              onClick={() => {
                // Reset user editing flag so setContent can run
                isUserEditingRef.current = false
                const cleanedContent = acceptAnnotations(brainstormContent)
                // Normalize to 4-space indentation for proper nested list rendering
                const normalizedContent = normalizeListIndentation(cleanedContent)
                updateBrainstormContent(normalizedContent)
                // Trigger immediate save to DB
                autoSave(normalizedContent)
              }}
              className="w-full text-xs bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20 transition-colors text-center py-2 rounded-md font-medium"
            >
              Accept All Changes
            </button>
          )}
          <button
            onClick={() => {
              // Normalize 2-space to 4-space indentation for proper nested list export
              const exportContent = normalizeListIndentation(brainstormContent)
              navigator.clipboard.writeText(exportContent)
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            }}
            className={`w-full text-xs text-center py-1 transition-colors ${
              copied
                ? 'text-green-600 dark:text-green-400 bg-green-500/10'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {copied ? 'Copied!' : 'Click to copy document'}
          </button>
        </div>
      )}
    </div>
  )
}
